// Pro-User-Auth-Settings (Push-Auth-Toggle + Reserve-Flags) plus
// system-weite Auth-Konfigs wie der Wrong-Code-Cooldown.
//
// Jeder PATCH gegen `/v2-api/auth/settings` braucht eine frische
// Passkey-Re-Auth-Assertion (Cookie aus /edit-challenge). Wir machen
// den Re-Auth-Tap hier in der gleichen Funktion -- der Caller (UI)
// liefert nur das Settings-Patch, die Bio-/Touch-Bestaetigung wird
// implizit angefragt.

import { getJSON, postJSON, patchJSON } from "./api";

export interface AuthSettingsView {
  // allow_push_auth ist seit dem Master-Switch-Refactor system-global
  // (lebt in settings.Store). Feldname bleibt fuer API-Stabilitaet.
  allow_push_auth: boolean;
  allow_magic_link: boolean;
  allow_otp_email: boolean;
  push_wrong_code_cooldown_s: number;
  // Auto-Off-Timer fuer den Push-Auth-Master-Switch (Minuten).
  // 0 = bleibt an bis manuell aus.
  push_auth_auto_off_min: number;
  // Zeitstempel wann der Master zuletzt eingeschaltet wurde (RFC3339).
  // Leer wenn aus. UI rechnet daraus die Restzeit fuer den Auto-Off.
  push_auth_enabled_at?: string;
  // Local-API-Bypass (system-global). Read-only hier mitgeliefert; das
  // Schreiben laeuft ueber POST /v2-api/settings/local-api-access (ohne
  // Re-Auth-Tap, eigener Endpoint).
  local_api_access: boolean;
  local_api_auto_off_min: number;
  local_api_enabled_at?: string;
  updated_at?: string;
}

export interface AuthSettingsPatch {
  allow_push_auth?: boolean;
  allow_magic_link?: boolean;
  allow_otp_email?: boolean;
  push_wrong_code_cooldown_s?: number;
  push_auth_auto_off_min?: number;
}

export async function getAuthSettings(): Promise<AuthSettingsView> {
  return getJSON<AuthSettingsView>("/v2-api/auth/settings");
}

// patchAuthSettings macht einen Re-Auth-Tap und sendet danach den
// PATCH. Falls der User die WebAuthn-Aufforderung abbricht, wird ein
// Error geworfen -- der Caller sollte dem User dann "Aenderung verworfen"
// zeigen.
export async function patchAuthSettings(
  changes: AuthSettingsPatch,
): Promise<AuthSettingsView> {
  const assertion = await frischeBestaetigung();
  return patchJSON<AuthSettingsView>("/v2-api/auth/settings", {
    ...changes,
    assertion,
  });
}

/** Eine frische Passkey-Bestätigung (ein Tipp auf den Passkey) für
 *  Eingriffe in die Anmeldung: Einstellungen ändern, einen Passkey
 *  entziehen (Repo-Prüfung 21.10.2026, Befund 11). Bricht der Nutzer ab,
 *  wirft sie. */
export async function frischeBestaetigung(): Promise<AssertionJSON> {
  const begin = await postJSON<{
    publicKey: PublicKeyCredentialRequestOptionsJSON;
  }>("/v2-api/auth/settings/edit-challenge");
  const opts = decodeRequestOptions(begin.publicKey);
  const cred = (await navigator.credentials.get({
    publicKey: opts,
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error("Keine WebAuthn-Antwort");
  return encodeAssertion(cred);
}

// Backwards-compat: bestehende Caller nutzen die Single-Field-Helper.
export async function setPushAuthAllowed(
  allowed: boolean,
): Promise<AuthSettingsView> {
  return patchAuthSettings({ allow_push_auth: allowed });
}

// --- WebAuthn-Codec (dupliziert aus pushAuth.svelte.ts; consolidate
// wenn ein dritter Caller dazukommt) -----------------------------------

interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: Array<{
    type: string;
    id: string;
    transports?: string[];
  }>;
  userVerification?: UserVerificationRequirement;
}

export interface AssertionJSON {
  id: string;
  rawId: string;
  type: string;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle?: string;
  };
}

function b64urlToBytes(s: string): ArrayBuffer {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const std = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(std);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function bytesToB64url(b: ArrayBuffer): string {
  const bytes = new Uint8Array(b);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function decodeRequestOptions(
  json: PublicKeyCredentialRequestOptionsJSON,
): PublicKeyCredentialRequestOptions {
  return {
    challenge: b64urlToBytes(json.challenge),
    timeout: json.timeout,
    rpId: json.rpId,
    userVerification: json.userVerification,
    allowCredentials: (json.allowCredentials ?? []).map((c) => ({
      type: c.type as PublicKeyCredentialType,
      id: b64urlToBytes(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };
}

function encodeAssertion(cred: PublicKeyCredential): AssertionJSON {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bytesToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bytesToB64url(r.clientDataJSON),
      authenticatorData: bytesToB64url(r.authenticatorData),
      signature: bytesToB64url(r.signature),
      userHandle: r.userHandle ? bytesToB64url(r.userHandle) : undefined,
    },
  };
}
